/**
 * Restriction enzymes supplied by New England Biolabs, from REBASE (Roberts et al., rebase.neb.com;
 * record dated September 2026). Each entry is the enzyme name and its recognition site in REBASE
 * notation: "^" marks the cut on the top strand of a symmetrical site, and "(a/b)" gives the
 * cut positions of a Type IIS enzyme as bases beyond the end of the site (top strand / bottom
 * strand; negative values fall inside the site). Letters are IUPAC nucleotide codes.
 *
 * Left out: homing endonucleases (I-CeuI, I-SceI, PI-PspI, PI-SceI), enzymes that cut on both
 * sides of their site (BaeI, BcgI, BsaXI, CspCI), the Type III EcoP15I and the
 * modification-dependent MspJI, none of which fit a simple one-site-one-cut model.
 */
export const ENZYME_TABLE: readonly (readonly [string, string])[] = [
  ["AatII", "GACGT^C"],
  ["AccI", "GT^MKAC"],
  ["Acc65I", "G^GTACC"],
  ["AciI", "CCGC(-3/-1)"],
  ["AclI", "AA^CGTT"],
  ["AcuI", "CTGAAG(16/14)"],
  ["AfeI", "AGC^GCT"],
  ["AflII", "C^TTAAG"],
  ["AflIII", "A^CRYGT"],
  ["AgeI", "A^CCGGT"],
  ["AhdI", "GACNNN^NNGTC"],
  ["AleI", "CACNN^NNGTG"],
  ["AluI", "AG^CT"],
  ["AlwI", "GGATC(4/5)"],
  ["AlwNI", "CAGNNN^CTG"],
  ["ApaI", "GGGCC^C"],
  ["ApaLI", "G^TGCAC"],
  ["ApeKI", "G^CWGC"],
  ["ApoI", "R^AATTY"],
  ["AscI", "GG^CGCGCC"],
  ["AseI", "AT^TAAT"],
  ["AsiSI", "GCGAT^CGC"],
  ["AvaI", "C^YCGRG"],
  ["AvaII", "G^GWCC"],
  ["AvrII", "C^CTAGG"],
  ["BaeGI", "GKGCM^C"],
  ["BamHI", "G^GATCC"],
  ["BanI", "G^GYRCC"],
  ["BanII", "GRGCY^C"],
  ["BbsI", "GAAGAC(2/6)"],
  ["BbvI", "GCAGC(8/12)"],
  ["BbvCI", "CCTCAGC(-5/-2)"],
  ["BccI", "CCATC(4/5)"],
  ["BceAI", "ACGGC(12/14)"],
  ["BciVI", "GTATCC(6/5)"],
  ["BclI", "T^GATCA"],
  ["BcoDI", "GTCTC(1/5)"],
  ["BfaI", "C^TAG"],
  ["BfuAI", "ACCTGC(4/8)"],
  ["BglI", "GCCNNNN^NGGC"],
  ["BglII", "A^GATCT"],
  ["BlpI", "GC^TNAGC"],
  ["BmgBI", "CACGTC(-3/-3)"],
  ["BmrI", "ACTGGG(5/4)"],
  ["BmtI", "GCTAG^C"],
  ["BpmI", "CTGGAG(16/14)"],
  ["Bpu10I", "CCTNAGC(-5/-2)"],
  ["BpuEI", "CTTGAG(16/14)"],
  ["BsaI", "GGTCTC(1/5)"],
  ["BsaAI", "YAC^GTR"],
  ["BsaBI", "GATNN^NNATC"],
  ["BsaHI", "GR^CGYC"],
  ["BsaJI", "C^CNNGG"],
  ["BsaWI", "W^CCGGW"],
  ["BseRI", "GAGGAG(10/8)"],
  ["BseYI", "CCCAGC(-5/-1)"],
  ["BsgI", "GTGCAG(16/14)"],
  ["BsiEI", "CGRY^CG"],
  ["BsiHKAI", "GWGCW^C"],
  ["BsiWI", "C^GTACG"],
  ["BslI", "CCNNNNN^NNGG"],
  ["BsmI", "GAATGC(1/-1)"],
  ["BsmAI", "GTCTC(1/5)"],
  ["BsmBI", "CGTCTC(1/5)"],
  ["BsmFI", "GGGAC(10/14)"],
  ["BsoBI", "C^YCGRG"],
  ["Bsp1286I", "GDGCH^C"],
  ["BspCNI", "CTCAG(9/7)"],
  ["BspDI", "AT^CGAT"],
  ["BspEI", "T^CCGGA"],
  ["BspHI", "T^CATGA"],
  ["BspMI", "ACCTGC(4/8)"],
  ["BspQI", "GCTCTTC(1/4)"],
  ["BsrI", "ACTGG(1/-1)"],
  ["BsrBI", "CCGCTC(-3/-3)"],
  ["BsrDI", "GCAATG(2/0)"],
  ["BsrFI", "R^CCGGY"],
  ["BsrGI", "T^GTACA"],
  ["BssHII", "G^CGCGC"],
  ["BssSI", "CACGAG(-5/-1)"],
  ["BstAPI", "GCANNNN^NTGC"],
  ["BstBI", "TT^CGAA"],
  ["BstEII", "G^GTNACC"],
  ["BstNI", "CC^WGG"],
  ["BstUI", "CG^CG"],
  ["BstXI", "CCANNNNN^NTGG"],
  ["BstYI", "R^GATCY"],
  ["BstZ17I", "GTA^TAC"],
  ["Bsu36I", "CC^TNAGG"],
  ["BtgI", "C^CRYGG"],
  ["BtgZI", "GCGATG(10/14)"],
  ["BtsI", "GCAGTG(2/0)"],
  ["BtsIMutI", "CAGTG(2/0)"],
  ["BtsCI", "GGATG(2/0)"],
  ["Cac8I", "GCN^NGC"],
  ["ClaI", "AT^CGAT"],
  ["CviKI-1", "RG^CY"],
  ["CviQI", "G^TAC"],
  ["DdeI", "C^TNAG"],
  ["DpnI", "GA^TC"],
  ["DpnII", "^GATC"],
  ["DraI", "TTT^AAA"],
  ["DraIII", "CACNNN^GTG"],
  ["DrdI", "GACNNNN^NNGTC"],
  ["EaeI", "Y^GGCCR"],
  ["EagI", "C^GGCCG"],
  ["EarI", "CTCTTC(1/4)"],
  ["EciI", "GGCGGA(11/9)"],
  ["EcoNI", "CCTNN^NNNAGG"],
  ["EcoO109I", "RG^GNCCY"],
  ["EcoRI", "G^AATTC"],
  ["EcoRV", "GAT^ATC"],
  ["Eco53kI", "GAG^CTC"],
  ["Esp3I", "CGTCTC(1/5)"],
  ["FatI", "^CATG"],
  ["FauI", "CCCGC(4/6)"],
  ["Fnu4HI", "GC^NGC"],
  ["FokI", "GGATG(9/13)"],
  ["FseI", "GGCCGG^CC"],
  ["FspI", "TGC^GCA"],
  ["HaeII", "RGCGC^Y"],
  ["HaeIII", "GG^CC"],
  ["HgaI", "GACGC(5/10)"],
  ["HhaI", "GCG^C"],
  ["HinP1I", "G^CGC"],
  ["HincII", "GTY^RAC"],
  ["HindIII", "A^AGCTT"],
  ["HinfI", "G^ANTC"],
  ["HpaI", "GTT^AAC"],
  ["HpaII", "C^CGG"],
  ["HphI", "GGTGA(8/7)"],
  ["Hpy99I", "CGWCG^"],
  ["Hpy166II", "GTN^NAC"],
  ["Hpy188I", "TCN^GA"],
  ["Hpy188III", "TC^NNGA"],
  ["HpyAV", "CCTTC(6/5)"],
  ["HpyCH4III", "ACN^GT"],
  ["HpyCH4IV", "A^CGT"],
  ["HpyCH4V", "TG^CA"],
  ["KasI", "G^GCGCC"],
  ["KpnI", "GGTAC^C"],
  ["MboI", "^GATC"],
  ["MboII", "GAAGA(8/7)"],
  ["MfeI", "C^AATTG"],
  ["MluI", "A^CGCGT"],
  ["MluCI", "^AATT"],
  ["MlyI", "GAGTC(5/5)"],
  ["MmeI", "TCCRAC(20/18)"],
  ["MnlI", "CCTC(7/6)"],
  ["MscI", "TGG^CCA"],
  ["MseI", "T^TAA"],
  ["MslI", "CAYNN^NNRTG"],
  ["MspI", "C^CGG"],
  ["MspA1I", "CMG^CKG"],
  ["MwoI", "GCNNNNN^NNGC"],
  ["NaeI", "GCC^GGC"],
  ["NarI", "GG^CGCC"],
  ["NciI", "CC^SGG"],
  ["NcoI", "C^CATGG"],
  ["NdeI", "CA^TATG"],
  ["NgoMIV", "G^CCGGC"],
  ["NheI", "G^CTAGC"],
  ["NlaIII", "CATG^"],
  ["NlaIV", "GGN^NCC"],
  ["NmeAIII", "GCCGAG(21/19)"],
  ["NotI", "GC^GGCCGC"],
  ["NruI", "TCG^CGA"],
  ["NsiI", "ATGCA^T"],
  ["NspI", "RCATG^Y"],
  ["PacI", "TTAAT^TAA"],
  ["PaeR7I", "C^TCGAG"],
  ["PaqCI", "CACCTGC(4/8)"],
  ["PciI", "A^CATGT"],
  ["PflFI", "GACN^NNGTC"],
  ["PflMI", "CCANNNN^NTGG"],
  ["PleI", "GAGTC(4/5)"],
  ["PluTI", "GGCGC^C"],
  ["PmeI", "GTTT^AAAC"],
  ["PmlI", "CAC^GTG"],
  ["PpuMI", "RG^GWCCY"],
  ["PshAI", "GACNN^NNGTC"],
  ["PsiI", "TTA^TAA"],
  ["PspGI", "^CCWGG"],
  ["PspOMI", "G^GGCCC"],
  ["PspXI", "VC^TCGAGB"],
  ["PstI", "CTGCA^G"],
  ["PvuI", "CGAT^CG"],
  ["PvuII", "CAG^CTG"],
  ["RsaI", "GT^AC"],
  ["RsrII", "CG^GWCCG"],
  ["SacI", "GAGCT^C"],
  ["SacII", "CCGC^GG"],
  ["SalI", "G^TCGAC"],
  ["SapI", "GCTCTTC(1/4)"],
  ["Sau96I", "G^GNCC"],
  ["Sau3AI", "^GATC"],
  ["SbfI", "CCTGCA^GG"],
  ["ScaI", "AGT^ACT"],
  ["ScrFI", "CC^NGG"],
  ["SexAI", "A^CCWGGT"],
  ["SfaNI", "GCATC(5/9)"],
  ["SfcI", "C^TRYAG"],
  ["SfiI", "GGCCNNNN^NGGCC"],
  ["SfoI", "GGC^GCC"],
  ["SgrAI", "CR^CCGGYG"],
  ["SmaI", "CCC^GGG"],
  ["SmlI", "C^TYRAG"],
  ["SnaBI", "TAC^GTA"],
  ["SpeI", "A^CTAGT"],
  ["SphI", "GCATG^C"],
  ["SrfI", "GCCC^GGGC"],
  ["SspI", "AAT^ATT"],
  ["StuI", "AGG^CCT"],
  ["StyI", "C^CWWGG"],
  ["StyD4I", "^CCNGG"],
  ["SwaI", "ATTT^AAAT"],
  ["TaqI", "T^CGA"],
  ["TfiI", "G^AWTC"],
  ["TseI", "G^CWGC"],
  ["Tsp45I", "^GTSAC"],
  ["TspMI", "C^CCGGG"],
  ["TspRI", "CASTGNN^"],
  ["Tth111I", "GACN^NNGTC"],
  ["XbaI", "T^CTAGA"],
  ["XcmI", "CCANNNNN^NNNNTGG"],
  ["XhoI", "C^TCGAG"],
  ["XmaI", "C^CCGGG"],
  ["XmnI", "GAANN^NNTTC"],
  ["ZraI", "GAC^GTC"],
];

/** The enzymes most often used for cloning and mapping, in a sensible order. */
export const POPULAR_ENZYMES: readonly string[] = [
  "EcoRI",
  "BamHI",
  "HindIII",
  "XhoI",
  "XbaI",
  "SalI",
  "PstI",
  "NotI",
  "SpeI",
  "KpnI",
  "SacI",
  "NcoI",
  "NdeI",
  "NheI",
  "BglII",
  "SmaI",
  "XmaI",
  "ApaI",
  "ClaI",
  "EcoRV",
  "MluI",
  "SphI",
  "PvuI",
  "PvuII",
  "BsaI",
  "BsmBI",
  "BbsI",
  "SapI",
  "AgeI",
  "AscI",
  "PacI",
  "SwaI",
  "FseI",
  "SbfI",
  "SfiI",
  "AflII",
  "BstBI",
  "BspEI",
  "BspHI",
  "HpaI",
  "StuI",
  "ScaI",
  "NruI",
  "PmeI",
  "SnaBI",
  "DraI",
  "HaeIII",
  "AluI",
  "MspI",
  "HpaII",
  "TaqI",
  "RsaI",
  "DdeI",
  "HinfI",
  "MseI",
  "Sau3AI",
  "NaeI",
  "NarI",
  "BsrGI",
  "BstEII",
  "Acc65I",
  "AvrII",
  "BclI",
  "AatII",
  "ApaLI",
  "AseI",
];
